import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { WHBABTEC_ENVIRONMENT } from '@whbabtec-environment';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  from,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { AuthService } from '../services/auth.service';
import { LanguageService } from '../services/language.service';

/** URLs that should never receive a Bearer token */
const TOKEN_EXCLUDED_URLS = [
  `${WHBABTEC_ENVIRONMENT.api.baseUrl}/api/v1/login/callback`,
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private router: Router,
    private languageService: LanguageService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const token = this.authService.getToken();
    const isExcluded = TOKEN_EXCLUDED_URLS.some((url) =>
      req.url.startsWith(url)
    );
    const authReq = token && !isExcluded ? this.addToken(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401 || !this.authService.hasRefreshToken()) {
          return throwError(() => error);
        }

        if (this.isRefreshing) {
          return this.refreshTokenSubject.pipe(
            filter((newToken) => newToken !== null),
            take(1),
            switchMap((newToken) =>
              next.handle(this.addToken(req, newToken as string))
            )
          );
        }

        return this.handleUnauthorized(error, req, next);
      })
    );
  }

  private addToken(
    req: HttpRequest<unknown>,
    token: string
  ): HttpRequest<unknown> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private handleUnauthorized(
    error: HttpErrorResponse,
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return from(this.authService.tryRefreshToken()).pipe(
      switchMap((refreshSucceeded) => {
        this.isRefreshing = false;

        if (refreshSucceeded) {
          const newToken = this.authService.getToken();
          this.refreshTokenSubject.next(newToken);
          const retryReq = newToken ? this.addToken(req, newToken) : req;
          return next.handle(retryReq);
        }

        console.warn('Token refresh failed — redirecting to login');
        return this.logoutAndRedirect(error);
      }),
      catchError((refreshError) => {
        this.isRefreshing = false;
        console.error('Error during token refresh:', refreshError);
        return this.logoutAndRedirect(error);
      })
    );
  }

  private logoutAndRedirect(
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    this.authService.logout();
    const lang = this.languageService.getCurrentLanguage();
    this.router.navigate([`/${lang}/login`]);
    return throwError(() => error);
  }
}
